import React from "react";
import { Link } from "react-router-dom";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { LeftArrowLine, RightArrowLine } from "../icons/Icons";

// Hooks and subcomponents
import { useSignupFlow } from "./hooks/useSignupFlow";
import SignupStepper from "./SignupStepper";
import AccountInfoStep from "./steps/AccountInfoStep";
import CompanyDetailsStep from "./steps/CompanyDetailsStep";
import VisualIdentityStep from "./steps/VisualIdentityStep";
import VerifyAccountStep from "./steps/VerifyAccountStep";

import PlanSummary from "./PlanSummary";

export default function SignUpStep({
  setCurrentTab,
  currentStep,
  setCurrentStep,
  registrationToken,
  setRegistrationToken,
  subscriptionPlan,
  isFlatPricing,
  quantity,
  inviteTokenData,
  inviteToken,
  couponCode,
  pricingData,
}) {
  const { t } = useTranslation("signup");
  const formikContext = useFormikContext();

  const { handleNext, handlePrev, handleResendOtp, isSubmitting, isResending } =
    useSignupFlow({
      currentStep,
      setCurrentStep,
      setCurrentTab,
      registrationToken,
      setRegistrationToken,
      planId: inviteTokenData ? inviteTokenData?.plan_id : subscriptionPlan?.id,
      quantity,
      inviteToken,
      couponCode,
      ...formikContext,
    });

  const getHeaderContent = () => {
    switch (currentStep) {
      case 2:
        return {
          title: t("company_details"),
          subtitle: t("tell_us_more"),
        };
      case 3:
        return {
          title: t("customize_platform"),
          subtitle: t("set_visual_identity"),
        };
      case 4:
        return {
          title: t("verify_account_title"),
          subtitle: t("verify_account_subtitle"),
        };
      default:
        return {
          title: t("create_new_account"),
          subtitle: t("welcome_to_tadarab"),
        };
    }
  };

  const { title, subtitle } = getHeaderContent();

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <AccountInfoStep />;
      case 2:
        return <CompanyDetailsStep />;
      case 3:
        return <VisualIdentityStep />;
      case 4:
        return (
          <VerifyAccountStep
            handleResendOtp={handleResendOtp}
            isResending={isResending}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return <AccountInfoStep />;
    }
  };

  return (
    <>
      <div className="step-content signup-process login">
        {currentStep <= 4 && (
          <div className="head-auth">
            <h2>{title}</h2>
            <p className="text-muted">{subtitle}</p>
          </div>
        )}

        <SignupStepper currentStep={currentStep} />

        <div key={currentStep}>
          {renderStep()}

          <div className="action-btns-sigup-process">
            {currentStep > 1 && currentStep < 4 && (
              <button
                onClick={handlePrev}
                className="btn btn-gray-border gap-2"
                type="button"
                disabled={isSubmitting}
              >
                <LeftArrowLine /> {t("previous")}
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              disabled={isSubmitting}
              className="btn btn-main-color gap-2 flex-1"
            >
              <span
                key={isSubmitting ? "loading" : "idle"}
                className="d-flex align-items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  <>
                    <span>
                      {currentStep === 1
                        ? t("next_company_details")
                        : currentStep === 2
                          ? t("next_visual_identity")
                          : currentStep === 3
                            ? t("create_account", "Create Account")
                            : t("verify_continue", "Verify & Continue")}
                    </span>
                    <RightArrowLine />
                  </>
                )}
              </span>
            </button>
          </div>
        </div>

        <div className="terms-condition">
          <p>
            {t("by_creating_account")}{" "}
            <button
              type="button"
              className="underline cursor-pointer color-white"
              style={{ background: "transparent", border: "none", padding: 0 }}
            >
              <Link to="/terms" key="terms" target="_blank">{t("terms_and_conditions")}</Link>
            </button>
          </p>
          <p className="md">
          </p>
        </div>
      </div>
      {inviteTokenData && (
        <PlanSummary
          planType={subscriptionPlan?.plan_type}
          pricingData={pricingData}
          quantity={quantity}
          isBasketStep={false}
          isInviteFlow={true}
          isFlatPricing={isFlatPricing}
        />
      )}
    </>
  );
}
